import React, { useCallback, useMemo } from "react";
import { Alert, Box, Card, CardContent, Typography } from "@mui/material";
import FiberManualRecord from "@mui/icons-material/FiberManualRecord";

import StatusChip from "@/components/muiCustom/chip/StatusChip";
import TableServer from "@/components/tables/TableServer";
import UserManagementActionsMenu from "./UserManagementActionsMenu";

type UserManagementAction = {
  value: string;
  label: string;
};

export type UserManagementRow = {
  id: number | string;
  actions: UserManagementAction[];
  dateAdded: string;
  email: string;
  jobTitle?: string;
  lastLogin: string;
  name: string;
  role: string;
  status: string;
};

type PaginationState = {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
};

type UserManagementTableProps = {
  users: UserManagementRow[];
  isLoading?: boolean;
  pagination: PaginationState;
  onFetchData: (_params: {
    pageIndex: number;
    pageSize: number;
    searchKey?: string;
  }) => void;
  onAction: (_action: string, _row: UserManagementRow) => void;
};

type UserTableTextProps = {
  children: React.ReactNode;
  component?: React.ElementType;
  className?: string;
} & Record<string, unknown>;

const UserTableText = ({
  children,
  component = "span",
  className = "",
  ...props
}: UserTableTextProps) => (
  <Box
    component={component}
    className={`text-break ${className}`.trim()}
    sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
    {...props}
  >
    {children}
  </Box>
);

type UserStatusBadgeProps = {
  status: string;
};

const UserStatusBadge = ({ status }: UserStatusBadgeProps) => {
  const statusColor =
    {
      Active: "var(--status-success)",
      "Invite Sent": "var(--status-warning)",
      Inactive: "var(--neutral-600)",
    }[status] || "inherit";

  return (
    <Box
      component="span"
      className="d-inline-flex align-items-center"
      style={{ color: statusColor, fontWeight: 400 }}
    >
      <FiberManualRecord
        className="mr-1"
        fontSize="inherit"
        htmlColor={statusColor}
      />
      {status}
    </Box>
  );
};

type UserRoleChipProps = {
  role: string;
};

const UserRoleChip = ({ role }: UserRoleChipProps) => {
  if (!role || role === "-") return "-";

  return (
    <StatusChip label={role} status={role === "Admin" ? "arStatus" : "info"} />
  );
};

type TableCellProps<TValue = unknown> = {
  value: TValue;
  row: {
    original: UserManagementRow;
  };
};

const UserManagementTable = ({
  users,
  isLoading,
  pagination,
  onFetchData,
  onAction,
}: UserManagementTableProps) => {
  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        width: 145,
        Cell: ({ value }: TableCellProps<React.ReactNode>) => (
          <UserTableText>{value}</UserTableText>
        ),
      },
      {
        Header: "Email",
        accessor: "email",
        width: 215,
        Cell: ({ value }: TableCellProps<React.ReactNode>) => (
          <UserTableText>{value}</UserTableText>
        ),
      },
      {
        Header: "Role",
        accessor: "role",
        width: 85,
        Cell: ({ value }: TableCellProps<string>) => (
          <UserRoleChip role={value} />
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        width: 110,
        Cell: ({ value }: TableCellProps<string>) => (
          <UserStatusBadge status={value} />
        ),
      },
      {
        Header: "Date Added",
        accessor: "dateAdded",
        width: 110,
      },
      {
        Header: "Job Title",
        accessor: "jobTitle",
        width: 125,
        Cell: ({ value }: TableCellProps<React.ReactNode>) => (
          <UserTableText>{value || "-"}</UserTableText>
        ),
      },
      {
        Header: "Last Login",
        accessor: "lastLogin",
        width: 110,
      },
      {
        id: "action",
        Header: "Action",
        accessor: "actions",
        disableSortBy: true,
        width: 75,
        Cell: ({ value, row }: TableCellProps<UserManagementAction[]>) => (
          <UserManagementActionsMenu
            actions={value}
            onAction={(action) => onAction(action, row.original)}
          />
        ),
      },
    ],
    [onAction]
  );

  const handleFetchData = useCallback(
    ({
      pageIndex,
      pageSize,
      searchKey,
    }: {
      pageIndex: number;
      pageSize: number;
      searchKey?: string;
    }) => {
      onFetchData({
        pageIndex,
        pageSize,
        searchKey,
      });
    },
    [onFetchData]
  );

  const renderTableTitle = useCallback(
    () => (
      <div className="col">
        <Typography variant="h6" className="table-title mb-0">
          Users
        </Typography>
      </div>
    ),
    []
  );

  return (
    <Card className="w-100 overflow-hidden px-0 py-4">
      <CardContent className="pt-0 pb-0 px-0">
        <TableServer
          columns={columns}
          data={users}
          isLoading={isLoading}
          pageCount={pagination.totalPages}
          totalRecords={pagination.totalRecords}
          initialPageSize={pagination.pageSize}
          initialPage={pagination.currentPage - 1}
          fetchData={handleFetchData}
          showPagination
          showPageSize={false}
          showSearch
          filters={renderTableTitle}
          hideSearchLabel
          containerClass="user-management-table"
          showSkeletonLoader
          skeletonRowCount={5}
          manualSortBy={false}
          emptyState={
            <Alert severity="info" className="my-0 text-left">
              No users found.
            </Alert>
          }
        />
      </CardContent>
    </Card>
  );
};

export default UserManagementTable;
